import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useGetAorsByHierarchy } from 'hooks/useGetAorsByHierarchy';
import { useAors } from 'hooks/useAors';
import type { AORHierarchyNode } from '../AorsTab/utils/schema';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { useAutoZoomToHomeAor } from '../AorsTab/utils/useAutoZoomToHomeAor';
import {
  resolveSitStatAorScope,
  type SitStatAorScope,
} from './sitstat-aor-scope';

type SitStatAorScopeContextValue = {
  /** `null` means every AOR. */
  scope: SitStatAorScope | null;
  selectedAorId: string | null;
  setSelectedAorId: (aorId: string | null) => void;
  hierarchy: AORHierarchyNode[] | undefined;
};

const SitStatAorScopeContext = createContext<SitStatAorScopeContextValue>({
  scope: null,
  selectedAorId: null,
  setSelectedAorId: () => {},
  hierarchy: undefined,
});

export function useSitStatAorScope() {
  return useContext(SitStatAorScopeContext);
}

type SitStatAorScopeProviderProps = {
  selectedAorId: string | null;
  onSelectedAorIdChange: (aorId: string | null) => void;
  children: ReactNode;
};

/**
 * Shares the SitStat AOR filter with every widget and mirrors it onto the map's
 * AOR polygons. The map filter is only cleared when this provider set it, so
 * opening SitStat unscoped leaves the user's map filter alone.
 */
export function SitStatAorScopeProvider({
  selectedAorId,
  onSelectedAorIdChange,
  children,
}: SitStatAorScopeProviderProps) {
  const { data: hierarchy } = useGetAorsByHierarchy();
  const { data: aors } = useAors();
  const { mapView, setAorFilterIds, setIsAorVisible } = useUscgMapContext();
  const { zoomToAorById } = useAutoZoomToHomeAor();

  const scope = useMemo(
    () => resolveSitStatAorScope(hierarchy, selectedAorId),
    [hierarchy, selectedAorId]
  );
  const scopeKey = scope ? scope.ids.join(',') : null;

  const appliedFilterRef = useRef(false);
  useEffect(() => {
    if (scopeKey === null) {
      if (appliedFilterRef.current) {
        appliedFilterRef.current = false;
        setAorFilterIds(null);
      }
      return;
    }
    appliedFilterRef.current = true;
    setIsAorVisible(true);
    setAorFilterIds(scopeKey.split(','));
  }, [scopeKey, setAorFilterIds, setIsAorVisible]);

  useEffect(() => {
    const applied = appliedFilterRef;
    return () => {
      if (applied.current) setAorFilterIds(null);
    };
  }, [setAorFilterIds]);

  const zoomedAorIdRef = useRef<string | null>(null);
  const canZoom =
    !!selectedAorId &&
    !!mapView &&
    !!aors?.some((aor) => String(aor.id) === selectedAorId);
  useEffect(() => {
    if (!selectedAorId) {
      zoomedAorIdRef.current = null;
      return;
    }
    if (!canZoom || zoomedAorIdRef.current === selectedAorId) return;
    zoomedAorIdRef.current = selectedAorId;
    void zoomToAorById(selectedAorId, { ensureVisible: true });
  }, [canZoom, selectedAorId, zoomToAorById]);

  const value = useMemo<SitStatAorScopeContextValue>(
    () => ({
      scope,
      selectedAorId,
      setSelectedAorId: onSelectedAorIdChange,
      hierarchy,
    }),
    [hierarchy, onSelectedAorIdChange, scope, selectedAorId]
  );

  return (
    <SitStatAorScopeContext.Provider value={value}>
      {children}
    </SitStatAorScopeContext.Provider>
  );
}
